import { MapTypeId } from "core/features/maps/enums";
import { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";
import delay from "delay";
import { MapRef, UseMapaParams } from "../typings";
import { DEFAULT_ZOOM } from "../config";
import { CenterMapContext } from "core/features/maps/center-map/CenterMap.context";
import { useFilters } from "core/features/filters/store";
import { PlanoOperacionalTalhao, TipoMapaTalhoes } from "core/features/maps/typings";
import useTalhoesStore from "./useTalhoesStore";
import {
  COLOR_DISPONIVEL,
  COLOR_TRANSPARENTE,
  COLOR_VINCULADO,
  getSelectedFillColor,
  SELECTED_STROKE,
  SELECTED_STROKE_WEIGHT,
  SELECTED_Z_INDEX,
} from "./talhaoSelectionColors";

interface TalhaoStyle {
  styleKey: string;
  fillColor: string;
  fillOpacity: number;
  strokeColor: string;
  strokeWeight: number;
  zIndex: number;
  isAbleToBind: boolean;
}

const IDLE_LABELS_DEBOUNCE_MS = 120;

export function useMap(params?: UseMapaParams) {
  const mapRef = useRef<MapRef>(null);
  const currentData = useRef<google.maps.Data.Feature[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const zoomRef = useRef(15);
  const [zoom, setZoom] = useState(15);
  // Zoom threshold only — never flipped mid-gesture.
  const [zoomAllowsMarkers, setZoomAllowsMarkers] = useState(false);
  // During pan/zoom, hide labels via visible=false (no remount) so Maps stays fluid.
  const [isInteracting, setIsInteracting] = useState(false);
  const isInteractingRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    filters: { tipoMapa },
  } = useFilters();
  const { shouldForceCenter, handleChangeShouldForceCenter } =
    useContext(CenterMapContext);

  const { planoOperacionalIds, talhaoIds, setTalhaoIds } = useTalhoesStore();

  const planoOperacionalIdsSet = useMemo(
    () => new Set(planoOperacionalIds.map((p) => p.id)),
    [planoOperacionalIds]
  );
  const ocupacoesSet = useMemo(
    () => new Set(planoOperacionalIds.map((p) => p.ocupacao)),
    [planoOperacionalIds]
  );
  const talhaoIdsSet = useMemo(
    () => new Set(talhaoIds.map((t) => t.id)),
    [talhaoIds]
  );

  const { onClickItem, points, center } = params ?? {};

  const beginInteraction = useCallback(() => {
    if (isInteractingRef.current) return;
    isInteractingRef.current = true;
    setIsInteracting(true);
  }, []);

  const syncAfterIdle = useCallback(() => {
    const nextZoom = mapRef.current?.getZoom() || 15;
    zoomRef.current = nextZoom;
    setZoom((prev) => (prev === nextZoom ? prev : nextZoom));
    setZoomAllowsMarkers((prev) => {
      const next = nextZoom >= 12;
      return prev === next ? prev : next;
    });
    if (isInteractingRef.current) {
      isInteractingRef.current = false;
      setIsInteracting(false);
    }
  }, []);

  const scheduleIdleSync = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    // Debounce rapid wheel zoom so labels don't thrash between notches.
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      syncAfterIdle();
    }, IDLE_LABELS_DEBOUNCE_MS);
  }, [syncAfterIdle]);

  const hideMarkers = useCallback(() => {}, []);

  const restoreMarkers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    syncAfterIdle();
  }, [syncAfterIdle]);

  const onLoad = useCallback(
    (map) => {
      const bounds = new google.maps.LatLngBounds();
      map.fitBounds(bounds);
      mapRef.current = map;

      const mapDiv = map.getDiv() as HTMLElement;
      mapDiv.style.outline = "none";
      mapDiv.setAttribute("tabindex", "-1");

      const hideMapsFocusRing = () => {
        const focusRing = mapDiv.querySelector(
          ".gm-style > iframe + div"
        ) as HTMLElement | null;
        if (focusRing) {
          focusRing.style.border = "none";
          focusRing.style.opacity = "0";
        }
        if (document.activeElement === mapDiv) {
          mapDiv.blur();
        }
      };

      mapDiv.addEventListener("focus", hideMapsFocusRing, true);
      map.addListener("click", hideMapsFocusRing);
      map.addListener("dragstart", hideMapsFocusRing);
      hideMapsFocusRing();

      setTimeout(() => setIsLoaded(true), 200);

      const initialZoom = map.getZoom() || 15;
      zoomRef.current = initialZoom;
      setZoom(initialZoom);
      setZoomAllowsMarkers(initialZoom >= 12);

      map.addListener("zoom_changed", () => {
        zoomRef.current = map.getZoom() || 15;
        beginInteraction();
      });

      map.addListener("dragstart", () => {
        beginInteraction();
      });

      map.addListener("idle", () => {
        scheduleIdleSync();
      });
    },
    [beginInteraction, scheduleIdleSync]
  );

  const onUnmount = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (typeof onClickItem !== "function" || !mapRef.current) {
      return;
    }

    const listener = mapRef.current.data.addListener("click", (ev: any) => {
      Object.keys(ev.feature).find((key) => {
        return !!ev.feature[key]?.id;
      });
    });

    return () => listener?.remove();
  }, [onClickItem]);

  function getStatusFillColor(planosTalhao: PlanoOperacionalTalhao[]): string {
    if (!Array.isArray(planosTalhao)) return COLOR_DISPONIVEL;
    for (const p of planosTalhao) {
      if (planoOperacionalIdsSet.has(p.PlanoOperacionalId)) {
        return COLOR_VINCULADO;
      }
    }
    return COLOR_DISPONIVEL;
  }

  function getTalhaoStyle(
    planosTalhao: PlanoOperacionalTalhao[],
    id: string,
    ocupacao: string
  ): TalhaoStyle {
    const base = (fillColor: string, isAbleToBind: boolean): TalhaoStyle => ({
      styleKey: fillColor,
      fillColor,
      fillOpacity: 1,
      strokeColor: "#FFFFFF",
      strokeWeight: 2,
      zIndex: 1,
      isAbleToBind,
    });

    if (!id || !ocupacao) {
      return base(COLOR_TRANSPARENTE, true);
    }

    if (!ocupacoesSet.has(ocupacao)) {
      return base(COLOR_TRANSPARENTE, false);
    }

    if (planoOperacionalIdsSet.size === 0) {
      return base(COLOR_TRANSPARENTE, false);
    }

    const statusColor = getStatusFillColor(planosTalhao);
    const isSelected = talhaoIdsSet.size > 0 && talhaoIdsSet.has(id);

    if (isSelected) {
      const fillColor = getSelectedFillColor(statusColor);
      return {
        styleKey: `selected:${statusColor}`,
        fillColor,
        fillOpacity: 1,
        strokeColor: SELECTED_STROKE,
        strokeWeight: SELECTED_STROKE_WEIGHT,
        zIndex: SELECTED_Z_INDEX,
        isAbleToBind: true,
      };
    }

    return base(statusColor, true);
  }

  useEffect(() => {
    if (!mapRef.current) return;

    mapRef.current.data.forEach((feature) => {
      const id = feature.getProperty("id");
      const ocupacao = feature.getProperty("ocupacaoNome");
      const planos = feature.getProperty("planosoperacionais") ?? [];

      const style = getTalhaoStyle(planos, id, ocupacao);
      const prevStyleKey = feature.getProperty("__color");

      feature.setProperty("isAbleToBind", style.isAbleToBind);

      if (prevStyleKey !== style.styleKey) {
        feature.setProperty("__color", style.styleKey);

        mapRef.current!.data.overrideStyle(feature, {
          fillColor: style.fillColor,
          fillOpacity: style.fillOpacity,
          strokeColor: style.strokeColor,
          strokeOpacity: 1,
          strokeWeight: style.strokeWeight,
          zIndex: style.zIndex,
        });
      }
    });
  }, [planoOperacionalIds, talhaoIds]);

  useEffect(() => {
    if (planoOperacionalIds.length === 0) {
      setTalhaoIds([]);
    }
  }, [planoOperacionalIds]);

  useEffect(() => {
    if (!points || !isLoaded || !mapRef.current) return;

    currentData.current.forEach((feature) =>
      mapRef.current?.data.remove(feature)
    );

    currentData.current = mapRef.current.data.addGeoJson(points);
  }, [points, isLoaded]);

  useEffect(() => {
    (async () => {
      if (
        !center ||
        !isLoaded ||
        !mapRef.current ||
        (!shouldForceCenter &&
          tipoMapa !== TipoMapaTalhoes.ConcentracaoColheita)
      ) {
        return;
      }
      await delay(200);

      mapRef.current?.setCenter({
        lat: center.lat,
        lng: center.long,
      });

      mapRef.current?.setZoom(DEFAULT_ZOOM);
      mapRef.current?.setMapTypeId(MapTypeId.HYBRID);

      syncAfterIdle();
      handleChangeShouldForceCenter(false);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, isLoaded, syncAfterIdle]);

  // Effective flags for consumers: hide during gesture, restore on idle.
  const displayMarkers = zoomAllowsMarkers && !isInteracting;
  const heavyOverlaysEnabled = !isInteracting;

  return {
    isLoaded,
    mapRef,
    onLoad,
    onUnmount,
    displayMarkers,
    zoomAllowsMarkers,
    hideMarkers,
    restoreMarkers,
    zoom,
    heavyOverlaysEnabled,
  };
}
